import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  ArrowDown,
  ArrowRight,
  Brain,
  CheckCircle2,
  Cpu,
  FileCode2,
  HardDrive,
  Laptop,
  Server,
  Terminal,
  XCircle,
  Zap,
} from "lucide-react";
import { DynamicSubtitle, SubtitleItem } from "./components/DynamicSubtitle";

// Timed subtitle segments matching Zura's actual audio
const SUBTITLES: SubtitleItem[] = [
  {
    from: 0,
    to: 115,
    text: "Banyak yang ngira kalau mau pasang autonomous AI agent kayak Hermes,",
    highlight: "autonomous AI agent kayak Hermes",
  },
  {
    from: 120,
    to: 265,
    text: "lo wajib punya laptop gaming spek dewa atau sewa VPS cloud puluhan dolar tiap bulan.",
    highlight: "laptop gaming spek dewa atau sewa VPS",
  },
  {
    from: 268,
    to: 310,
    text: "Padahal ini mitos.",
    highlight: "Padahal ini mitos",
  },
  {
    from: 315,
    to: 440,
    text: "Hermes Agent itu enteng banget, cuma butuh Python dan terminal biasa.",
    highlight: "cuma butuh Python dan terminal biasa",
  },
  {
    from: 445,
    to: 650,
    text: "Laptop kantor RAM 8GB, MacBook Air tipis, sampai mini PC bekas pun bisa lo sulap jadi workstation AI otonom.",
    highlight: "MacBook Air tipis & mini PC bekas",
  },
  {
    from: 655,
    to: 675,
    text: "Kenapa?",
    highlight: "Kenapa?",
  },
  {
    from: 680,
    to: 770,
    text: "Karena proses mikirnya tetep dilempar ke Cloud API.",
    highlight: "proses mikirnya dilempar ke Cloud API",
  },
  {
    from: 775,
    to: 850,
    text: "Sementara laptop lo cuma fokus ngejalanin tugas lokalnya.",
    highlight: "cuma fokus tugas lokalnya",
  },
  {
    from: 850,
    to: 980,
    text: "Ngebaca file, ngetik kode, dan eksekusi skrip di disk lo sendiri.",
    highlight: "baca file, ngetik kode, eksekusi skrip",
  },
  {
    from: 985,
    to: 1110,
    text: "Lo gak butuh server mahal buat punya asisten cerdas yang beneran kerja di sebelah lo.",
    highlight: "gak butuh server mahal",
  },
  {
    from: 1115,
    to: 1280,
    text: "Follow @zura.ailab buat liat cara setup dan eksperimen sistem AI lainnya.",
    highlight: "Follow @zura.ailab",
  },
];

// FONT DECLARATIONS VIA STATICFILE
const fontFaces = `
@font-face {
  font-family: 'TekoDisplay';
  src: url('${staticFile("fonts/Teko-700.woff2")}') format('woff2');
  font-weight: 700;
  font-style: normal;
}
@font-face {
  font-family: 'BebasDisplay';
  src: url('${staticFile("fonts/BebasNeue-400.woff2")}') format('woff2');
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: 'AntonDisplay';
  src: url('${staticFile("fonts/Anton-400.woff2")}') format('woff2');
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: 'RajdhaniDisplay';
  src: url('${staticFile("fonts/Rajdhani-700.woff2")}') format('woff2');
  font-weight: 700;
  font-style: normal;
}
@font-face {
  font-family: 'ChakraDisplay';
  src: url('${staticFile("fonts/ChakraPetch-700.woff2")}') format('woff2');
  font-weight: 700;
  font-style: normal;
}
`;

export const ZuraHermesHardwareMyth: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Snappy spring helper
  const sp = (delay: number, damping = 16, stiffness = 130) =>
    spring({
      frame: Math.max(0, frame - delay),
      fps,
      config: { damping, stiffness },
    });

  // Scene transitions
  const s1Exit = interpolate(frame, [290, 310], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s1Opacity = 1 - s1Exit;
  const s1Y = interpolate(s1Exit, [0, 1], [0, -32]);

  const s2Exit = interpolate(frame, [435, 450], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s2Enter = interpolate(frame, [300, 320], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s2Opacity = s2Enter * (1 - s2Exit);
  const s2Y = interpolate(s2Exit, [0, 1], [0, -32]);

  const s3Exit = interpolate(frame, [645, 665], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s3Enter = interpolate(frame, [440, 460], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s3Opacity = s3Enter * (1 - s3Exit);
  const s3Y = interpolate(s3Exit, [0, 1], [0, -32]);

  const s4Exit = interpolate(frame, [965, 985], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s4Enter = interpolate(frame, [655, 675], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s4Opacity = s4Enter * (1 - s4Exit);
  const s4Y = interpolate(s4Exit, [0, 1], [0, -32]);

  const s5Enter = interpolate(frame, [975, 995], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s5Opacity = s5Enter;

  // Stamp bounce at frame 268 ("Padahal ini mitos")
  const stampSpring = spring({
    frame: Math.max(0, frame - 265),
    fps,
    config: { damping: 12, stiffness: 200 },
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#F6F4EB", // OpenDesign Warm Paper
        color: "#1C1917",
        fontFamily: "'Inter', sans-serif",
        overflow: "hidden",
      }}
    >
      <style>{fontFaces}</style>

      {/* AUDIO: Zura Mastered VO + Ducked BGM */}
      <Audio src={staticFile("vo_zura_hermes_local.mp3")} volume={1.0} />
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.08} loop />

      {/* TACTILE PAPER GRAIN & FINE PIN-BOARD DOT GRID */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle, rgba(28, 25, 23, 0.08) 1.5px, transparent 1.5px)",
          backgroundSize: "32px 32px",
          pointerEvents: "none",
        }}
      />

      {/* SUBTLE PAPER VIGNETTE BORDER */}
      <div
        style={{
          position: "absolute",
          inset: 24,
          border: "2.5px dashed rgba(28, 25, 23, 0.16)",
          borderRadius: 40,
          pointerEvents: "none",
        }}
      />

      {/* TOP HEADER BRAND CAPSULE (SAFE ZONE Y: 260px) */}
      <div
        style={{
          position: "absolute",
          top: 260,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          zIndex: 80,
          transform: `translateY(${interpolate(sp(0, 18, 120), [0, 1], [-20, 0])}px)`,
          opacity: sp(0, 18, 120),
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            backgroundColor: "#FFFFFF",
            padding: "12px 28px",
            borderRadius: 999,
            border: "3px solid #1C1917",
            boxShadow: "5px 5px 0px #1C1917",
          }}
        >
          <img
            src={staticFile("assets_hermes_local/hermes_winged_helmet_logo.jpg")}
            alt="Hermes"
            style={{ width: 26, height: 26, borderRadius: 6, objectFit: "cover" }}
          />
          <span
            style={{
              fontFamily: "'RajdhaniDisplay', sans-serif",
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: 2,
              color: "#1C1917",
              textTransform: "uppercase",
            }}
          >
            HERMES AGENT · HARDWARE MYTH BUSTED
          </span>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#EA580C",
            }}
          />
        </div>
      </div>

      {/* ========================================================= */}
      {/* EDITORIAL STAGE CANVAS (Y: 340px - 1420px, WIDTH: 880px)  */}
      {/* Centered with generous breathing room & Swiss typography  */}
      {/* ========================================================= */}
      <div
        style={{
          position: "absolute",
          top: 340,
          bottom: 500,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 50,
        }}
      >
        <div
          style={{
            width: 880,
            height: "100%",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {/* ========================================================= */}
          {/* SCENE 1: THE DUAL MYTH — GAMING LAPTOP & VPS (0 - 310)    */}
          {/* ========================================================= */}
          {frame < 315 && (
            <div
              style={{
                opacity: s1Opacity,
                transform: `translateY(${s1Y}px)`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 20,
                width: "100%",
                position: "relative",
              }}
            >
              <div style={{ textAlign: "center", width: "100%" }}>
                <span
                  style={{
                    backgroundColor: "#DC2626",
                    color: "#FFFFFF",
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 20,
                    fontWeight: 700,
                    letterSpacing: 2,
                    padding: "6px 20px",
                    borderRadius: 999,
                    display: "inline-block",
                    marginBottom: 10,
                  }}
                >
                  MITOS SPEK HARDWARE AI
                </span>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 78,
                    lineHeight: 0.95,
                    letterSpacing: 1.5,
                    color: "#1C1917",
                    textTransform: "uppercase",
                  }}
                >
                  WAJIB LAPTOP GAMING & VPS?
                </div>
              </div>

              {/* 2 Myth Cards Side by Side */}
              <div style={{ display: "flex", gap: 18, width: "100%" }}>
                {/* Myth Card 1: Gaming Laptop */}
                <div
                  style={{
                    flex: 1,
                    backgroundColor: "#FFFFFF",
                    border: "3.5px solid #1C1917",
                    borderRadius: 24,
                    padding: 20,
                    boxShadow: "6px 6px 0px #1C1917",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: 180,
                      borderRadius: 16,
                      overflow: "hidden",
                      border: "2px solid #1C1917",
                      backgroundColor: "#0C0A09",
                    }}
                  >
                    <img
                      src={staticFile("assets_hermes_local/gen_gaming_laptop.jpg")}
                      alt="Gaming Laptop"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </div>
                  <div
                    style={{
                      fontFamily: "'BebasDisplay', sans-serif",
                      fontSize: 30,
                      color: "#DC2626",
                      marginTop: 14,
                      lineHeight: 1.0,
                    }}
                  >
                    LAPTOP GAMING SPEK DEWA
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: "#4B5563", marginTop: 6, lineHeight: 1.3 }}>
                    GPU Monster, Fan Bising & Berat Banget
                  </div>
                </div>

                {/* Myth Card 2: Cloud Server */}
                <div
                  style={{
                    flex: 1,
                    backgroundColor: "#FFFFFF",
                    border: "3.5px solid #1C1917",
                    borderRadius: 24,
                    padding: 20,
                    boxShadow: "6px 6px 0px #1C1917",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: 180,
                      borderRadius: 16,
                      overflow: "hidden",
                      border: "2px solid #1C1917",
                      backgroundColor: "#0C0A09",
                    }}
                  >
                    <img
                      src={staticFile("assets_hermes_local/gen_server_rack.jpg")}
                      alt="Server Rack"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </div>
                  <div
                    style={{
                      fontFamily: "'BebasDisplay', sans-serif",
                      fontSize: 30,
                      color: "#DC2626",
                      marginTop: 14,
                      lineHeight: 1.0,
                    }}
                  >
                    SEWA VPS CLOUD RATUSAN RIBU
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: "#4B5563", marginTop: 6, lineHeight: 1.3 }}>
                    Beban Tagihan Rutin $10 - $50 / Bulan
                  </div>
                </div>
              </div>

              {/* Massive Diagonal Stamp on frame >= 265 ("Padahal ini mitos") */}
              {frame >= 260 && (
                <div
                  style={{
                    position: "absolute",
                    top: "42%",
                    left: "5%",
                    right: "5%",
                    backgroundColor: "#DC2626",
                    color: "#FFFFFF",
                    border: "5px solid #1C1917",
                    borderRadius: 24,
                    padding: "20px 24px",
                    textAlign: "center",
                    boxShadow: "10px 10px 0px #1C1917",
                    transform: `rotate(-8deg) scale(${stampSpring})`,
                    zIndex: 99,
                  }}
                >
                  <div
                    style={{
                      fontFamily: "'AntonDisplay', sans-serif",
                      fontSize: 64,
                      lineHeight: 0.95,
                      letterSpacing: 2,
                      textTransform: "uppercase",
                    }}
                  >
                    ⚠ 100% SALAH KAPRAH!
                  </div>
                  <div
                    style={{
                      fontFamily: "'RajdhaniDisplay', sans-serif",
                      fontSize: 26,
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      marginTop: 6,
                    }}
                  >
                    GAK PERLU GPU MELEDUK & GAK WAJIB SEWA SERVER!
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ========================================================= */}
          {/* SCENE 2: HERMES REALITY — PYTHON & CLI (305 - 450)        */}
          {/* ========================================================= */}
          {frame >= 300 && frame < 455 && (
            <div
              style={{
                opacity: s2Opacity,
                transform: `translateY(${s2Y}px)`,
                display: "flex",
                flexDirection: "column",
                gap: 20,
                width: "100%",
                alignItems: "center",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <span
                  style={{
                    backgroundColor: "#059669",
                    color: "#FFFFFF",
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 20,
                    fontWeight: 700,
                    letterSpacing: 2,
                    padding: "6px 20px",
                    borderRadius: 999,
                    display: "inline-block",
                    marginBottom: 10,
                  }}
                >
                  FAKTA ARSITEKTUR HERMES
                </span>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 76,
                    lineHeight: 0.95,
                    color: "#1C1917",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  }}
                >
                  HERMES ITU SUPER ENTENG!
                </div>
              </div>

              {/* Main Bento Hero Card: Hermes Official Banner & Icon */}
              <div
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "4px solid #1C1917",
                  borderRadius: 28,
                  padding: "28px 32px",
                  boxShadow: "8px 8px 0px #1C1917",
                  width: "100%",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  gap: 24,
                }}
              >
                <div
                  style={{
                    width: 110,
                    height: 110,
                    borderRadius: 24,
                    overflow: "hidden",
                    border: "3.5px solid #1C1917",
                    boxShadow: "4px 4px 0px #1C1917",
                    flexShrink: 0,
                    backgroundColor: "#000000",
                  }}
                >
                  <img
                    src={staticFile("assets_hermes_local/hermes_winged_helmet_logo.jpg")}
                    alt="Hermes Winged Helmet"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <img
                    src={staticFile("assets_hermes_local/hermes_agent_banner_official.png")}
                    alt="Hermes Agent Official"
                    style={{ height: 36, objectFit: "contain", marginBottom: 8 }}
                  />
                  <div
                    style={{
                      fontFamily: "'RajdhaniDisplay', sans-serif",
                      fontSize: 26,
                      fontWeight: 700,
                      color: "#1C1917",
                      lineHeight: 1.2,
                    }}
                  >
                    AUTONOMOUS AI AGENT DENGAN NATIVE TOOL-CALLING
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 600, color: "#4B5563", marginTop: 4 }}>
                    Arsitektur lean tanpa overhead GUI berat.
                  </div>
                </div>
              </div>

              {/* 2 Requirements Pills (Python + Terminal) */}
              <div style={{ display: "flex", gap: 16, width: "100%" }}>
                <div
                  style={{
                    flex: 1,
                    backgroundColor: "#EFF6FF",
                    border: "3.5px solid #2563EB",
                    borderRadius: 22,
                    padding: "20px 24px",
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    boxShadow: "5px 5px 0px #2563EB",
                  }}
                >
                  <img
                    src={staticFile("assets_hermes_local/icon_python.svg")}
                    alt="Python"
                    style={{ width: 44, height: 44 }}
                  />
                  <div>
                    <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 32, color: "#1E40AF", lineHeight: 1.0 }}>
                      CUMA PYTHON 3.10+
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: "#3B82F6", marginTop: 2 }}>
                      Runtime standar & enteng
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    flex: 1,
                    backgroundColor: "#ECFDF5",
                    border: "3.5px solid #059669",
                    borderRadius: 22,
                    padding: "20px 24px",
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    boxShadow: "5px 5px 0px #059669",
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      backgroundColor: "#059669",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Terminal size={28} color="#FFFFFF" />
                  </div>
                  <div>
                    <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 32, color: "#065F46", lineHeight: 1.0 }}>
                      TERMINAL BIASA
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: "#059669", marginTop: 2 }}>
                      CLI bawaan OS tanpa emulasi
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCENE 3: HARDWARE FLEET (440 - 665)                       */}
          {/* ========================================================= */}
          {frame >= 435 && frame < 670 && (
            <div
              style={{
                opacity: s3Opacity,
                transform: `translateY(${s3Y}px)`,
                display: "flex",
                flexDirection: "column",
                gap: 16,
                width: "100%",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <span
                  style={{
                    backgroundColor: "#EA580C",
                    color: "#FFFFFF",
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 20,
                    fontWeight: 700,
                    letterSpacing: 2,
                    padding: "6px 20px",
                    borderRadius: 999,
                    display: "inline-block",
                    marginBottom: 8,
                  }}
                >
                  FLEKSIBILITAS TANPA BATAS
                </span>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 72,
                    lineHeight: 0.95,
                    color: "#1C1917",
                    textTransform: "uppercase",
                  }}
                >
                  SULAP PERANGKAT APAPUN
                </div>
              </div>

              {/* 3 Horizontal Hardware Bento Rows */}
              <div style={{ display: "flex", flexDirection: "column", gap: 14, width: "100%" }}>
                {/* Hardware 1: MacBook Air & Office Laptop */}
                <div
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "3.5px solid #1C1917",
                    borderRadius: 22,
                    padding: "16px 22px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxShadow: "5px 5px 0px #1C1917",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <div
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: 14,
                        overflow: "hidden",
                        border: "2px solid #1C1917",
                        backgroundColor: "#F3F4F6",
                      }}
                    >
                      <img
                        src={staticFile("assets_hermes_local/gen_thin_laptop.jpg")}
                        alt="Thin Laptop"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    </div>
                    <div>
                      <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 32, color: "#1C1917", lineHeight: 1.0 }}>
                        MACBOOK AIR / LAPTOP KANTOR RAM 8GB
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 600, color: "#4B5563", marginTop: 4 }}>
                        Bodi tipis, dingin, & baterai tahan seharian
                      </div>
                    </div>
                  </div>
                  <span style={{ backgroundColor: "#ECFDF5", border: "2px solid #059669", color: "#065F46", fontSize: 16, fontWeight: 800, padding: "6px 14px", borderRadius: 8 }}>
                    READY 100%
                  </span>
                </div>

                {/* Hardware 2: Mini PC */}
                <div
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "3.5px solid #1C1917",
                    borderRadius: 22,
                    padding: "16px 22px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxShadow: "5px 5px 0px #1C1917",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <div
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: 14,
                        overflow: "hidden",
                        border: "2px solid #1C1917",
                        backgroundColor: "#F3F4F6",
                      }}
                    >
                      <img
                        src={staticFile("assets_hermes_local/gen_mini_pc.jpg")}
                        alt="Mini PC"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    </div>
                    <div>
                      <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 32, color: "#1C1917", lineHeight: 1.0 }}>
                        COMPACT MINI PC (DESKTOP CUBE)
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 600, color: "#4B5563", marginTop: 4 }}>
                        Workstation mandiri 24/7 cuma 15 Watt
                      </div>
                    </div>
                  </div>
                  <span style={{ backgroundColor: "#EFF6FF", border: "2px solid #2563EB", color: "#1E40AF", fontSize: 16, fontWeight: 800, padding: "6px 14px", borderRadius: 8 }}>
                    ALWAYS-ON
                  </span>
                </div>

                {/* Hardware 3: Raspberry Pi & NUC Bekas */}
                <div
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "3.5px solid #1C1917",
                    borderRadius: 22,
                    padding: "16px 22px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxShadow: "5px 5px 0px #1C1917",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <div
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: 14,
                        overflow: "hidden",
                        border: "2px solid #1C1917",
                        backgroundColor: "#F3F4F6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={staticFile("assets_hermes_local/raspberry_pi_4b.png")}
                        alt="Raspberry Pi"
                        style={{ width: "90%", height: "90%", objectFit: "contain" }}
                      />
                    </div>
                    <div>
                      <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 32, color: "#1C1917", lineHeight: 1.0 }}>
                        RASPBERRY PI 4 / INTEL NUC BEKAS
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 600, color: "#4B5563", marginTop: 4 }}>
                        Hardware murah disulap jadi bot AI otonom
                      </div>
                    </div>
                  </div>
                  <span style={{ backgroundColor: "#FFF7ED", border: "2px solid #EA580C", color: "#9A3412", fontSize: 16, fontWeight: 800, padding: "6px 14px", borderRadius: 8 }}>
                    BUDGET KING
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCENE 4: 2-TIER ARCHITECTURE (655 - 985)                  */}
          {/* ========================================================= */}
          {frame >= 650 && frame < 990 && (
            <div
              style={{
                opacity: s4Opacity,
                transform: `translateY(${s4Y}px)`,
                display: "flex",
                flexDirection: "column",
                gap: 16,
                width: "100%",
                alignItems: "center",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <span
                  style={{
                    backgroundColor: "#1C1917",
                    color: "#FFFFFF",
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 20,
                    fontWeight: 700,
                    letterSpacing: 2,
                    padding: "6px 20px",
                    borderRadius: 999,
                    display: "inline-block",
                    marginBottom: 8,
                  }}
                >
                  RAHASIA DI BALIK LAYAR
                </span>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 70,
                    lineHeight: 0.95,
                    color: "#1C1917",
                    textTransform: "uppercase",
                  }}
                >
                  ARSITEKTUR DUA LAPIS
                </div>
              </div>

              {/* TIER 1: CLOUD API LAYER (BRAIN) */}
              <div
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "3.5px solid #2563EB",
                  borderRadius: 24,
                  padding: "20px 26px",
                  width: "100%",
                  boxSizing: "border-box",
                  boxShadow: "6px 6px 0px #2563EB",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Brain size={28} color="#2563EB" />
                    <span style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 32, color: "#1E40AF" }}>
                      1. CLOUD API LAYER · PROSES MIKIR BERAT
                    </span>
                  </div>
                  <span style={{ backgroundColor: "#2563EB", color: "#FFFFFF", fontSize: 14, fontWeight: 800, padding: "4px 12px", borderRadius: 6 }}>
                    SERVER LUAR
                  </span>
                </div>
                {/* Model Logo Row */}
                <div style={{ display: "flex", alignItems: "center", gap: 16, backgroundColor: "#F8FAFC", padding: "10px 16px", borderRadius: 14, border: "1.5px solid #E2E8F0" }}>
                  <img src={staticFile("assets_hermes_local/icon_anthropic.svg")} alt="Claude" style={{ width: 28, height: 28 }} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: "#334155" }}>Claude 3.7</span>
                  <div style={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: "#94A3B8" }} />
                  <img src={staticFile("assets_hermes_local/icon_openai.svg")} alt="OpenAI" style={{ width: 26, height: 26 }} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: "#334155" }}>GPT-5 / o3</span>
                  <div style={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: "#94A3B8" }} />
                  <img src={staticFile("assets_hermes_local/icon_google_gemini.svg")} alt="Gemini" style={{ width: 26, height: 26 }} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: "#334155" }}>Gemini</span>
                  <div style={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: "#94A3B8" }} />
                  <img src={staticFile("assets_hermes_local/icon_deepseek.svg")} alt="DeepSeek" style={{ width: 26, height: 26 }} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: "#334155" }}>DeepSeek</span>
                </div>
              </div>

              {/* DOWN ARROW CONNECTOR */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  backgroundColor: "#1C1917",
                  color: "#FFFFFF",
                  padding: "6px 20px",
                  borderRadius: 999,
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: 1,
                  boxShadow: "3px 3px 0px rgba(0,0,0,0.2)",
                }}
              >
                <span>KIRIM FUNCTION CALL / PERINTAH KERJA</span>
                <ArrowDown size={18} color="#00F0FF" />
              </div>

              {/* TIER 2: LOCAL HARDWARE LAYER (HANDS) */}
              <div
                style={{
                  backgroundColor: "#ECFDF5",
                  border: "3.5px solid #059669",
                  borderRadius: 24,
                  padding: "20px 26px",
                  width: "100%",
                  boxSizing: "border-box",
                  boxShadow: "6px 6px 0px #059669",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <HardDrive size={28} color="#059669" />
                    <span style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 32, color: "#065F46" }}>
                      2. LOCAL HARDWARE · EKSEKUSI TUNTAS DI DISK
                    </span>
                  </div>
                  <span style={{ backgroundColor: "#059669", color: "#FFFFFF", fontSize: 14, fontWeight: 800, padding: "4px 12px", borderRadius: 6 }}>
                    ENTENG BANGET
                  </span>
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ flex: 1, backgroundColor: "#FFFFFF", border: "2px solid #059669", borderRadius: 14, padding: "10px 12px", textAlign: "center" }}>
                    <div style={{ fontWeight: 800, fontSize: 16, color: "#065F46" }}>1. BACA FILE</div>
                    <div style={{ fontSize: 14, color: "#4B5563" }}>Disk I/O lokal</div>
                  </div>
                  <div style={{ flex: 1, backgroundColor: "#FFFFFF", border: "2px solid #059669", borderRadius: 14, padding: "10px 12px", textAlign: "center" }}>
                    <div style={{ fontWeight: 800, fontSize: 16, color: "#065F46" }}>2. TULIS KODE</div>
                    <div style={{ fontSize: 14, color: "#4B5563" }}>Script generation</div>
                  </div>
                  <div style={{ flex: 1, backgroundColor: "#FFFFFF", border: "2px solid #059669", borderRadius: 14, padding: "10px 12px", textAlign: "center" }}>
                    <div style={{ fontWeight: 800, fontSize: 16, color: "#065F46" }}>3. RUN TERMINAL</div>
                    <div style={{ fontSize: 14, color: "#4B5563" }}>CLI Execution</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCENE 5: OUTRO & CALL TO ACTION (980 - 1280)              */}
          {/* ========================================================= */}
          {frame >= 975 && (
            <div
              style={{
                opacity: s5Opacity,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 22,
                width: "100%",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 26,
                    fontWeight: 700,
                    letterSpacing: 2,
                    color: "#DC2626",
                    textTransform: "uppercase",
                  }}
                >
                  STOP BAYAR SERVER CLOUD MAHAL
                </div>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 80,
                    lineHeight: 0.95,
                    color: "#1C1917",
                    marginTop: 8,
                    textTransform: "uppercase",
                  }}
                >
                  ASISTEN CERDAS DI LAPTOP LO
                </div>
              </div>

              {/* 3 Benefit Pills */}
              <div style={{ display: "flex", justifyContent: "space-between", gap: 14, width: "100%" }}>
                <div style={{ flex: 1, backgroundColor: "#FFFFFF", border: "3px solid #1C1917", borderRadius: 20, padding: "16px 14px", textAlign: "center", boxShadow: "5px 5px 0px #1C1917" }}>
                  <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 28, color: "#1C1917" }}>01 · TANPA VPS</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#6B7280", marginTop: 2 }}>Nol Biaya Server</div>
                </div>
                <div style={{ flex: 1, backgroundColor: "#FFFFFF", border: "3px solid #1C1917", borderRadius: 20, padding: "16px 14px", textAlign: "center", boxShadow: "5px 5px 0px #1C1917" }}>
                  <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 28, color: "#1C1917" }}>02 · RAM 8GB CUKUP</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#6B7280", marginTop: 2 }}>MacBook / Mini PC</div>
                </div>
                <div style={{ flex: 1, backgroundColor: "#FFFFFF", border: "3px solid #1C1917", borderRadius: 20, padding: "16px 14px", textAlign: "center", boxShadow: "5px 5px 0px #1C1917" }}>
                  <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 28, color: "#1C1917" }}>03 · TOOL NYATA</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#6B7280", marginTop: 2 }}>Terminal & File Lokal</div>
                </div>
              </div>

              {/* Massive CTA Box */}
              <div
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "4px solid #1C1917",
                  borderRadius: 28,
                  padding: "24px 32px",
                  width: "100%",
                  boxShadow: "10px 10px 0px #1C1917",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxSizing: "border-box",
                  marginTop: 6,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                  <div
                    style={{
                      width: 68,
                      height: 68,
                      borderRadius: "50%",
                      backgroundColor: "#000000",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "3px solid #1C1917",
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ fontFamily: "monospace", fontSize: 30, fontWeight: 900, color: "#00F0FF" }}>&#123;</span>
                    <span style={{ fontFamily: "'AntonDisplay', sans-serif", fontSize: 32, fontWeight: 900, color: "#FFFFFF", marginLeft: 1 }}>Z</span>
                  </div>
                  <div>
                    <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 38, lineHeight: 1.0, color: "#1C1917" }}>
                      ZURA · AI & FUTURE TECH
                    </div>
                    <div style={{ fontFamily: "'RajdhaniDisplay', sans-serif", fontSize: 22, fontWeight: 700, color: "#059669", marginTop: 4 }}>
                      @ZURA.AILAB · AGENT & MOTION
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: "#EA580C",
                    color: "#FFFFFF",
                    padding: "16px 28px",
                    borderRadius: 999,
                    border: "3px solid #1C1917",
                    boxShadow: "4px 4px 0px #1C1917",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 24,
                    fontWeight: 700,
                    letterSpacing: 1.5,
                  }}
                >
                  <span>PANTAU CARANYA</span>
                  <ArrowRight size={26} color="#FFFFFF" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* DYNAMIC TIMED SUBTITLE (ANCHORED AT BOTTOM: 490px / SAFE ZONE Y: 1430px) */}
      <DynamicSubtitle
        frame={frame}
        subtitles={SUBTITLES}
        bottom={490}
        fontSize={34}
        highlightColor="#F59E0B"
        backgroundColor="rgba(28, 25, 23, 0.94)"
      />
    </AbsoluteFill>
  );
};
